'use client';

import { PropsWithChildren, useEffect, useState } from 'react';
import { useI18n } from 'next-localization';

import { LinkButton } from '@/components/link-button';
import { cn } from '@/lib/utils';
import { NeuIcon } from '@hcahealthcaredpx/neutron-react';
import { RichText } from '@sitecore-jss/sitecore-jss-nextjs';
import { MediaConnectVideo } from '@xsf/shared-lib/components/media-connect';

import styles from '../profile.module.css';
import { BodySectionProps } from '../profile.types';
import { changeVideoUrl, createWebsiteUrl } from '../utils';

const Section = ({ children, title }: PropsWithChildren<{ title: string }>) => {
  return (
    <div className="row col-12">
      <div className="col-md-3 d-flex mb-2">
        <div className={cn('ps-0', styles.sectionTitleWrapper)}>
          <h5 className={cn('fw-bold', styles.sectionTitle)}>{title}</h5>
        </div>
      </div>
      <div className="col-md-9">
        <div className={styles.sectionContent}>{children}</div>
      </div>
    </div>
  );
};

const MAXIMUM_INITIAL_CHARACTERS_SHOW = 500;

//  TODO: Think of moving this on a separate file and make this file server side
const BioSectionRow = ({ text }: { text: string }) => {
  const { t } = useI18n();

  const [viewMore, setViewMore] = useState(false);

  return (
    <Section title={t('OAS.Profile.Details.Biography')}>
      <RichText
        field={{
          value: viewMore ? text : text?.substring(0, MAXIMUM_INITIAL_CHARACTERS_SHOW),
        }}
        tag="div"
      />
      {text?.length > MAXIMUM_INITIAL_CHARACTERS_SHOW ? (
        <LinkButton
          onClick={() => setViewMore((p) => !p)}
          className="mt-2"
          aria-expanded={viewMore}
        >
          {viewMore ? t('OAS.Profile.Details.CloseBio') : t('OAS.Profile.Details.ViewFullBio')}
          <NeuIcon name={viewMore ? 'keyboard_arrow_up' : 'keyboard_arrow_down'} size="2x" />
        </LinkButton>
      ) : null}
    </Section>
  );
};

export const BodySection = ({
  featureVideos,
  pageTitle,
  physicianBio,
  physicianCarePhilosophy,
  proceduresAndConditions,
  providerSpecialties,
  affiliations,
  website,
  credentialsAndAccreditations,
}: BodySectionProps): JSX.Element => {
  const { t } = useI18n();
  // TODO: Take isPhysicians from shared hook when its created / useSiteType() /
  const isPhysicians = false;

  const uniqueProcedures = Array.from(new Set(proceduresAndConditions.map((cond) => cond.keyword)));

  const featuredVideos = featureVideos.filter(({ featured }) => featured);

  const notFeaturedVideos = featureVideos.filter(({ featured }) => !featured);

  const physicianLinks = () => {
    if (!featureVideos || !featureVideos.length) return [];

    return featureVideos.filter(({ type }) => type.toLowerCase() === 'link');
  };

  const physicianPublications = () => {
    if (!featureVideos || !featureVideos.length) return [];
    return featureVideos.filter(({ type }) => type.toLowerCase() === 'publication');
  };

  useEffect(() => {
    const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    const scrollToProceduresAndConditions =
      params.get('scroll-to-procedures-and-conditions') === 'true';

    if (scrollToProceduresAndConditions) {
      const proceduresElement = document.getElementById('procedures-and-conditions');
      if (proceduresElement) {
        proceduresElement.scrollIntoView({ behavior: 'instant' });
      }
    }
  }, []);

  return (
    <div className="neu-bg-plain-0">
      <div className="container">
        <div className="row mb-6">
          <div className="col-12">
            <div className={cn('row d-flex justify-content-center', styles.wrapper)}>
              <h5 className={cn('col-12 text-center', styles.title)}>
                {t('OAS.Profile.TitlePrefix')} {pageTitle}
              </h5>

              {featuredVideos?.length > 0 ? (
                <div className={cn('col-9', styles.featureVideoWrapper)}>
                  <div className={styles.embadedContainer}>
                    <MediaConnectVideo
                      video={
                        featuredVideos?.[0].url.includes('youtu')
                          ? changeVideoUrl(featuredVideos?.[0])
                          : featuredVideos?.[0].url
                      }
                      title={featuredVideos?.[0].name}
                      imageClasses="max-h-256"
                    />
                  </div>
                </div>
              ) : null}

              {physicianBio && <BioSectionRow text={physicianBio} />}
              {physicianCarePhilosophy && (
                <Section title={t('OAS.Profile.Details.CarePhilosophy')}>
                  <RichText field={{ value: physicianCarePhilosophy }} tag="div" />
                </Section>
              )}
              {proceduresAndConditions?.length > 0 && (
                <Section title={t('OAS.Profile.Details.ProceduresAndConditionsTreated')}>
                  <ul id="procedures-and-conditions">
                    {uniqueProcedures.map((el, index) => (
                      <li key={`${el}${+index}`}>{el}</li>
                    ))}
                  </ul>
                </Section>
              )}

              {providerSpecialties?.length > 0 && (
                <Section title={t('OAS.Profile.Details.Specialties')}>
                  <ul id="specialties">
                    {providerSpecialties.map((el, index) => (
                      <li key={`${el.specialty}${+index}`}>
                        <span className="fw-semibold">{el.specialty}</span>
                        {el.boardCertified ? ` - ${t('OAS.Profile.Details.BoardCertified')}` : null}
                      </li>
                    ))}
                  </ul>
                </Section>
              )}

              {affiliations?.length > 0 && (
                <Section title={t('OAS.Profile.Details.Affiliations')}>
                  <ul id="affiliations">
                    {affiliations.map((el, index) => (
                      <li key={`${el.locationCOID}${+index}`}>{el.locationName}</li>
                    ))}
                  </ul>
                </Section>
              )}

              {website && !isPhysicians && (
                <Section title={t('OAS.Profile.Details.ClinicWebsite')}>
                  <ul id="websites">
                    <li>
                      <a
                        href={createWebsiteUrl(website)}
                        target="_blank"
                        rel="noreferrer"
                        className={styles.clinicWebsiteLink}
                      >
                        {website}
                      </a>
                    </li>
                  </ul>
                </Section>
              )}

              {credentialsAndAccreditations?.length > 0 && (
                <Section title={t('OAS.Profile.Details.CredentialsAndEducation')}>
                  <ul id="credentials">
                    {credentialsAndAccreditations.map((el, index) => (
                      <li key={`${el.credentialOrAccreditationId}${+index}`}>
                        <p className="fw-semibold neu-text-gray-100">{el.organizationOrSchool}</p>
                        <p>{el.type}</p>
                        <p>{el.yearReceived}</p>
                      </li>
                    ))}
                  </ul>
                </Section>
              )}

              {featuredVideos?.length >= 2 || notFeaturedVideos.length ? (
                <Section title={t('OAS.Profile.Details.Media')}>
                  <ul id="featured-videos">
                    {featuredVideos.slice(1).map((el, index) => (
                      <li key={`${el.name}${+index}`}>
                        <a href={createWebsiteUrl(el.url)} target="_blank" rel="noreferrer">
                          {el.name}
                        </a>
                      </li>
                    ))}

                    {notFeaturedVideos.map((el, index) => (
                      <li key={`${el.name}${+index}`}>
                        <a href={createWebsiteUrl(el.url)} target="_blank" rel="noreferrer">
                          {el.name}
                        </a>
                      </li>
                    ))}
                  </ul>
                </Section>
              ) : null}

              {physicianLinks()?.length > 0 ? (
                <Section title={t('OAS.Profile.Details.OtherLinks')}>
                  <ul id="other-links">
                    {physicianLinks().map((el, index) => (
                      <li key={`${el.name}${+index}`}>
                        <a href={createWebsiteUrl(el.url)} target="_blank" rel="noreferrer">
                          {el.name}
                        </a>
                      </li>
                    ))}
                  </ul>
                </Section>
              ) : null}

              {physicianPublications()?.length > 0 ? (
                <Section title={t('OAS.Profile.Details.Publications')}>
                  <ul id="publications">
                    {physicianPublications().map((el, index) => (
                      <li key={`${el.name}${+index}`}>
                        <a href={createWebsiteUrl(el.url)} target="_blank" rel="noreferrer">
                          {el.name}
                        </a>
                      </li>
                    ))}
                  </ul>
                </Section>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
